"use client"

import { useState } from "react"
import { useScriptPlaybackStore } from "@/lib/script-playback-store"
import { useGlobalPlaybackStore } from "@/lib/global-playback-store"
import { Button } from "@/components/ui/button"
import { Play, Pause, X, ScrollText } from "@/lib/icons"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"
import { QuickConnectDeviceModal } from "@/components/quick-connect-device-modal"

interface ScriptMiniPlayerProps {
  onOpenModal?: () => void
}

export function ScriptMiniPlayer({ onOpenModal }: ScriptMiniPlayerProps) {
  const t = useTranslations("scriptMiniPlayer")
  const activeScriptId = useScriptPlaybackStore((s) => s.activeScriptId)
  const activeScriptName = useScriptPlaybackStore((s) => s.activeScriptName)
  const isPlaying = useScriptPlaybackStore((s) => s.isPlaying)
  const currentTime = useScriptPlaybackStore((s) => s.currentTime)
  const totalDuration = useScriptPlaybackStore((s) => s.totalDuration)
  const isDeviceConnected = useScriptPlaybackStore((s) => s.isDeviceConnected)
  const controls = useScriptPlaybackStore((s) => s.controls)
  const activeSource = useGlobalPlaybackStore((s) => s.activeSource)
  // This dot was a pure status display, site-wide, everywhere this mini
  // player shows while browsing -- red with no way to act on it, forcing a
  // trip to the Devices tab even though a script is already playing right
  // here. Same fix as the other device-status indicators this session.
  const [showQuickConnect, setShowQuickConnect] = useState(false)

  const handlePlayPause = () => {
    if (!controls) return
    if (isPlaying) controls.pause()
    else controls.resume()
  }

  const handleStop = () => {
    if (controls) controls.stop()
    else useScriptPlaybackStore.getState().stopScript()
  }

  if (!activeScriptId || !activeScriptName) return null
  if (activeSource === "game-arena") return null
  if (activeScriptId.startsWith("curve-")) return null

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-background/80 backdrop-blur-md border border-border/60 shadow-xl shadow-black/20 min-w-0 max-w-72">

        {/* Status dot + name */}
        {isDeviceConnected ? (
          <div className="h-1.5 w-1.5 rounded-full shrink-0 bg-green-400" />
        ) : (
          <button
            type="button"
            onClick={() => setShowQuickConnect(true)}
            title={t("connectADevice")}
            className="h-2.5 w-2.5 shrink-0 flex items-center justify-center"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
          </button>
        )}
        <ScrollText className="h-3.5 w-3.5 text-purple-400 shrink-0" />
        <span className="text-xs font-medium truncate flex-1 min-w-0">{activeScriptName}</span>

        {/* Time */}
        <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
          {formatTime(currentTime)}<span className="opacity-50">/{formatTime(totalDuration)}</span>
        </span>


        {/* Controls */}
        <Button
          size="icon" variant="ghost"
          className="h-6 w-6 shrink-0"
          onClick={handlePlayPause}
        >
          {isPlaying
            ? <Pause className="h-3 w-3" />
            : <Play className="h-3 w-3" />
          }
        </Button>

        {onOpenModal && (
          <button
            type="button"
            onClick={onOpenModal}
            className="text-[10px] text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            {t("open")}
          </button>
        )}

        <Button
          size="icon" variant="ghost"
          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={handleStop}
        >
          <X className="h-3 w-3" />
        </Button>
        <QuickConnectDeviceModal open={showQuickConnect} onClose={() => setShowQuickConnect(false)} />
    </div>
  )
}
